/* eslint-disable prettier/prettier */
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useContext } from "react";

import { SimpleSnackbarContext } from "../components/SimpleSnackbar";
import { language } from "../language";
import { customFetch } from "../util/customFetch";

export function useProcessDefinitionsDrafts({ enabled = true, params }) {
  const page = params.page;
  const size = params.pageSize;
  const sortOrder = params.sortOrder || "desc";
  const sort = params.sortBy
    ? `${params.sortBy},${sortOrder}`
    : params.sort
      ? `${params.sort},${sortOrder}`
      : "";
  const queryParams = { page, size, sortOrder, sort };
  return useQuery({
    enabled,
    queryKey: ["process-definitions-drafts", queryParams],
    queryFn: async () =>
      customFetch(
        `/api/smartbpm/v1/{tenant}/process-definition-drafts?${new URLSearchParams(queryParams)}`,
      ),
  });
}

export function useProcessDefinitionDraftFindByProcessKey() {
  return useMutation({
    mutationFn: async ({ processKey }) =>
      customFetch(
        `/api/smartbpm/v1/{tenant}/process-definition-drafts?processKey=${processKey}`,
      ),
  });
}

export function useProcessDefinitionDraftDelete({ params }) {
  const queryClient = useQueryClient();
  const { actions: snackActions } = useContext(SimpleSnackbarContext);
  return useMutation({
    mutationFn: async (id) =>
      await customFetch(
        `/api/smartbpm/v1/{tenant}/process-definition-drafts/${id}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["process-definitions-drafts", params],
      });
    },
    onError: (error) => {
      snackActions.open(`${error}`, { severity: "error" });
    },
  });
}

export function useProcessDefinitionDraftCreate() {
  const { actions: snackActions } = useContext(SimpleSnackbarContext);
  return useMutation({
    mutationFn: async (payload) => {
      const body = new FormData();
      const blob = new Blob([payload.file], { type: "text/xml" });

      body.append("diagram", blob, payload.filename);

      body.append("processName", payload.filename.replace(/.bpmn$/, ""));
      body.append("processKey", payload.processKey);
      body.append("category", payload.category);
      return await customFetch(
        `/api/smartbpm/v1/{tenant}/process-definition-drafts`,
        {
          method: "POST",
          body,
        },
      );
    },
    onSuccess: () => {
      snackActions.open(language.draftUpdated, { severity: "success" });
    },
    onError: (error) => {
      console.error("Error saving draft", error);
      snackActions.open(language.errorDraftSave, { severity: "error" });
    },
  });
}

export function useProcessDefinitionDraftUpdate() {
  const { actions: snackActions } = useContext(SimpleSnackbarContext);
  return useMutation({
    mutationFn: async ({ id, payload }) => {
      const body = new FormData();
      const blob = new Blob([payload.file], { type: "text/xml" });

      body.append("diagram", blob, payload.filename);

      body.append("processName", payload.filename.replace(/.bpmn$/, ""));
      body.append("processKey", payload.processKey);
      body.append("category", payload.category);

      return await customFetch(
        `/api/smartbpm/v1/{tenant}/process-definition-drafts/${id}`,
        {
          method: "PUT",
          body,
        },
      );
    },
    onSuccess: () => {
      snackActions.open(language.draftSaved, { severity: "success" });
    },
    onError: (error) => {
      snackActions.open(language.errorDraftSave, { severity: "error" });
    },
  });
}

export function useProcessDefinitionsDraftDownload({ enabled = true, id }) {
  return useQuery({
    enabled,
    queryKey: ["process-definitions-drafts", "download", id],
    queryFn: async () =>
      await customFetch(
        `/api/smartbpm/v1/{tenant}/process-definition-drafts/${id}/download`,
      ),
  });
}
